import { Fragment, useState, useCallback } from "react";
import {
  ActionType,
  ClientIntegration,
  IntegrationType,
  useTriggerActionLazyQuery,
} from "~/operations";
import { IntegrationRecommendedPolicies } from "../../../components/IntegrationRecommendedPolicies";
import { createPath, parsePath } from "react-router-dom";
import { IntegrationAddHeader } from "../../../headers/IntegrationAddHeader";
import { UpdateFlowData } from "../../../utils/integrationsTypes";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { TabPanel } from "~/components/tab-panel";
import { AzureAutomatedForm } from "~/pages/integrations/hosted-integrations/forms/azure/components/AzureAutomatedForm";
import { AzureManualForm } from "~/pages/integrations/hosted-integrations/forms/azure/components/AzureManualForm";
import { useCanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { useCanUpdate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Update";
import useGenerateIntegrationName from "../../../utils/integrationsUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

export function AzureIntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  const [createdIntegrationMrn, setCreatedIntegrationMrn] = useState<
    ClientIntegration["mrn"] | undefined
  >(undefined);
  const [view, setView] = useState<"setup" | "add policies">("setup");
  const nextDefaultIntegrationName = useGenerateIntegrationName({ scope });
  const [defaultIntegrationName, _setDefaultIntegrationName] = useState<string>(
    () => nextDefaultIntegrationName || "new-azure-integration",
  );

  const canCreateIntegration = useCanCreate();
  const canUpdateIntegration = useCanUpdate();

  const hasAutomatedDeploymentPermissions = updateFlow
    ? canUpdateIntegration
    : canCreateIntegration;

  const [TriggerClientIntegrationScan] = useTriggerActionLazyQuery();

  const handleTriggerScan = useCallback(async () => {
    if (!createdIntegrationMrn) return;
    try {
      await TriggerClientIntegrationScan({
        variables: {
          input: { mrn: createdIntegrationMrn, type: ActionType.RunScan },
        },
      });
    } catch (e) {
      console.error(e);
    }
  }, [createdIntegrationMrn]);

  const integrationPath = `/space/integrations/add/mondoo/azure?${scope.params}`;

  const generateHref = (tab: string): string => {
    const parsedPath = parsePath(integrationPath);
    const pathname = `${parsedPath.pathname}/${tab}`;
    const search = parsedPath.search;
    return createPath({ pathname, search });
  };

  const tabList: TabListItem[] = [
    {
      label: "Automated setup",
      to: generateHref("automated"),
      route: "/automated",
    },
    {
      label: "Manual setup",
      to: generateHref("manual"),
      route: "/manual",
    },
  ];

  // if updateFlow exists, we should default to the manual tab
  const initialTab = updateFlow ? "manual" : "automated";

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    initialTab,
  );

  return (
    <Fragment>
      {view === "setup" ? (
        <Fragment>
          <IntegrationAddHeader {...{ type: IntegrationType.Azure }} />
          <Fragment>
            <TabNavigation
              id={`${IntegrationType.Azure}-tabs`}
              tabList={tabList}
              currentTab={currentTab}
            />
            <TabPanel value={currentTab} index="/automated">
              <AzureAutomatedForm
                {...{
                  defaultIntegrationName,
                  scope,
                  setView,
                  hasAutomatedDeploymentPermissions,
                }}
              />
            </TabPanel>
            <TabPanel value={currentTab} index="/manual">
              <AzureManualForm
                {...{
                  defaultIntegrationName,
                  scope,
                  setView,
                  updateFlow,
                  setCreatedIntegrationMrn,
                }}
              />
            </TabPanel>
          </Fragment>
        </Fragment>
      ) : (
        <IntegrationRecommendedPolicies
          {...{ scope }}
          filterTypes={["azure"]}
          bonusMessage="azure-cloud"
          finalizeAction={handleTriggerScan}
        />
      )}
    </Fragment>
  );
}
